import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, ChevronLeft } from "lucide-react";
import api, { money, fmtErr } from "@/lib/api";
import Logo from "@/components/Logo";
import { FreePill } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", card: "", size: "" });

  useEffect(() => { api.get(`/products/${slug}`).then((r) => setP(r.data)); }, [slug]);
  useEffect(() => { if (user) setF((s) => ({ ...s, name: user.name || "", email: user.email || "" })); }, [user]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        product_slug: slug, name: f.name, email: f.email, phone: f.phone,
        address: f.address, city: f.city, state: f.state, zip: f.zip, size: f.size,
      });
      nav(`/upsell/${data.id}`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); setBusy(false); }
  };

  if (!p) return <div className="min-h-screen grid place-items-center text-stone-400">Loading…</div>;
  const tax = +(p.shipping * 0.07).toFixed(2);
  const total = +(p.shipping + tax).toFixed(2);

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-2xl"><Logo /></Link>
          <span className="flex items-center gap-1.5 text-sm text-stone-500"><Lock size={14} /> Secure checkout</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to={`/friib/${slug}`} className="inline-flex items-center gap-1 text-sm font-semibold text-stone-500 hover:text-stone-900 mb-4"><ChevronLeft size={16} /> Back</Link>
        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          <form onSubmit={submit} className="space-y-6" data-testid="checkout-form">
            <Block title="Contact">
              <Field label="Full name" value={f.name} onChange={set("name")} tid="checkout-name" required />
              <Field label="Email" type="email" value={f.email} onChange={set("email")} tid="checkout-email" required />
              <Field label="Phone (optional)" value={f.phone} onChange={set("phone")} tid="checkout-phone" />
            </Block>
            <Block title="Shipping address">
              <Field label="Street address" value={f.address} onChange={set("address")} tid="checkout-address" required />
              <div className="grid grid-cols-3 gap-3">
                <Field label="City" value={f.city} onChange={set("city")} tid="checkout-city" required />
                <Field label="State" value={f.state} onChange={set("state")} tid="checkout-state" required />
                <Field label="ZIP" value={f.zip} onChange={set("zip")} tid="checkout-zip" required />
              </div>
              {p.category === "fashion" && <Field label="Size" value={f.size} onChange={set("size")} tid="checkout-size" placeholder="e.g. M" />}
            </Block>
            <Block title="Payment">
              <p className="text-xs text-stone-400 mb-2">Demo checkout — no real charge. Enter any test number.</p>
              <Field label="Card number" value={f.card} onChange={set("card")} tid="checkout-card" placeholder="4242 4242 4242 4242" required />
            </Block>
            <button disabled={busy} data-testid="place-order-btn"
              className="w-full py-4 rounded-full font-extrabold text-white text-lg transition-transform hover:scale-[1.01] disabled:opacity-60" style={{ background: "var(--friib)" }}>
              {busy ? "Securing your Friib…" : `Claim Friib · ${money(total)}`}
            </button>
          </form>

          {/* SUMMARY */}
          <aside className="lg:sticky lg:top-6 h-fit">
            <div className="rounded-2xl bg-white border border-stone-100 friib-shadow p-6" data-testid="order-summary">
              <div className="flex gap-4 pb-5 border-b border-stone-200">
                <img src={p.images[0]} alt={p.name} className="w-20 h-20 object-cover rounded-xl" />
                <div>
                  <p className="text-xs font-bold uppercase text-stone-500">{p.brand_name}</p>
                  <p className="font-display font-bold text-stone-900 leading-tight">{p.name}</p>
                  <div className="mt-1"><FreePill /></div>
                </div>
              </div>
              <div className="py-5 space-y-3 text-sm border-b border-stone-200">
                <Row label="Product" value={money(0)} strike={money(p.retail_value)} />
                <Row label="Shipping & Handling" value={money(p.shipping)} />
                <Row label="Taxes" value={money(tax)} />
              </div>
              <div className="pt-4 flex items-center justify-between">
                <span className="font-display font-bold text-lg text-stone-900">Total</span>
                <span className="font-display font-extrabold text-2xl text-stone-900">{money(total)}</span>
              </div>
              <p className="mt-4 text-xs text-stone-400 text-center">You clearly pay only disclosed shipping & handling. The product itself is free.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Block({ title, children }) {
  return <div className="rounded-2xl bg-white border border-stone-100 friib-shadow p-6"><h3 className="font-display font-bold text-stone-900 mb-4">{title}</h3><div className="space-y-3">{children}</div></div>;
}
function Field({ label, value, onChange, type = "text", required, placeholder, tid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-stone-500">{label}</span>
      <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={tid}
        className="mt-1 w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:border-[var(--friib)] outline-none" />
    </label>
  );
}
function Row({ label, value, strike }) {
  return <div className="flex items-center justify-between"><span className="text-stone-500">{label}</span><span className="font-semibold text-stone-900">{strike && <span className="text-stone-400 line-through mr-2 font-normal">{strike}</span>}{value}</span></div>;
}
